import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import { LoadingOverlay } from '@/Components/shared';
import useCandidatesResultsContextValue from './Contexts/useCandidatesResultsContextValue';
import { CandidatesResultsContext } from './Contexts/CandidateResultsContext';
import ApplicantResultsHeader from './Components/ApplicantResultsHeader';
import ResultsTabContent from './Components/ResultsTabContent';
import withTalentMeshUserRequired from '@/Routing/withTalentMeshUserRequired';
import withSignupChecks from '@/Routing/withSignupChecks';
import withCompanyProfileRequired from '@/Routing/withCompanyProfileRequired';
import ResultsPageDialogs from './Components/ResultsPageDialogs';
import { useAssessmentSettings } from '@/api/useAssessmentSettings';
import useActionDialogContextValue from '../components/dialogs/Context/ActionDialogContextValue';
import { ActionDialogContext } from '../components/dialogs/Context/ActionDialogContext';
import Page from '@/Components/page';

interface CandidateResultsPageParams {
    candidateId: string;
    assessmentId: string;
}

function CandidateResultsPage(): React.JSX.Element {
    const contextValue = useCandidatesResultsContextValue();
    const dialogContextValue = useActionDialogContextValue();
    const { data: assessmentSettings, isLoading: isAssessmentSettingsLoading } = useAssessmentSettings();
    const { loadCandidateResultsAsync, isLoading } = contextValue;
    const { assessmentId, candidateId } = useParams<Partial<CandidateResultsPageParams>>();

    useEffect(() => {
        if (!isAssessmentSettingsLoading) {
            loadCandidateResultsAsync(assessmentId!, candidateId!, assessmentSettings.jobCategories);
        }
    }, [isAssessmentSettingsLoading, assessmentSettings.jobCategories]);

    return (
        <CandidatesResultsContext.Provider value={contextValue}>
            <ActionDialogContext.Provider value={dialogContextValue}>
                <Page>
                    {isLoading ? (
                        <LoadingOverlay />
                    ) : (
                        <div data-cy="pageContentContainer" className="bg-muted flex h-full flex-col overflow-auto">
                            <ApplicantResultsHeader />
                            <div className="container max-w-7xl place-self-center pb-4">
                                <ResultsTabContent />
                                <ResultsPageDialogs />
                            </div>
                        </div>
                    )}
                </Page>
            </ActionDialogContext.Provider>
        </CandidatesResultsContext.Provider>
    );
}

export default withCompanyProfileRequired(withTalentMeshUserRequired(withSignupChecks(CandidateResultsPage)));
